<template>
    <div
        class="flex-col items-center justify-center"
        :class="[displayClass, rootClass]"
        :style="rootStyle"
    >
        <activix-ui-spinner
            v-bind="spinnerAttrs"
            :disable-default-colors="true"
            :class="spinnerClass"
            :style="spinnerStyle"
            :width="spinnerWidth"
        />
        <span class="mt-2 text-center text-sm text-gray-700" v-if="message">
            {{ message }}
        </span>
    </div>
</template>

<script lang="ts" setup>
    import { computed, normalizeClass, useAttrs } from 'vue';
    import ActivixUiSpinner from '@/components/activix-ui/ActivixSpinner/ActivixSpinner.vue';
    import { SpinnerWidth } from '@/components/activix-ui/ActivixSpinner/types.ts';

    defineOptions({
        name: 'ActivixSpinner',
        inheritAttrs: false,
        // FIXME(VUE3): Remove after dropping @vue/compat — Vue 3 includes class/style in $attrs by default
        compatConfig: { INSTANCE_ATTRS_CLASS_STYLE: false },
    });

    interface Props {
        size?: number;
        message?: string;
        lineSize?: number;
    }

    const props = withDefaults(defineProps<Props>(), {
        size: 60,
        message: '',
        lineSize: 4,
    });

    const attrs = useAttrs();

    const normalizedClassNames = computed(() => {
        return normalizeClass(attrs.class);
    });

    const classNames = computed(() => {
        return normalizedClassNames.value.split(/\s+/).filter(Boolean);
    });

    const isDisplayClass = (className: string): boolean => {
        return (
            className === 'inline-block' || className === 'inline-flex' || className === 'inline'
        );
    };

    const isPseudoElementClass = (className: string): boolean => {
        return className.includes('before:') || className.includes('after:');
    };

    // Match Tailwind "before:border-<color>" classes while excluding numeric width and opacity utilities.
    const isBeforeBorderColorClass = (className: string): boolean => {
        return /^before:border-(?!opacity-)(?!\d)/.test(className);
    };

    // Match Tailwind "after:border-t-<color>" classes while excluding numeric width and opacity utilities.
    const isAfterBorderTopColorClass = (className: string): boolean => {
        return /^after:border-t-(?!opacity-)(?!\d)/.test(className);
    };

    const displayClass = computed(() => {
        const callerDisplay = classNames.value.find(isDisplayClass);
        return callerDisplay ?? 'flex';
    });

    const rootClass = computed(() => {
        return classNames.value.filter(
            (className) => !isPseudoElementClass(className) && !isDisplayClass(className)
        );
    });

    const rootStyle = computed(() => attrs.style);

    const pseudoElementClassNames = computed(() => {
        return classNames.value.filter((className) => isPseudoElementClass(className));
    });

    const compatColorClasses = computed(() => {
        return [
            !pseudoElementClassNames.value.some(isBeforeBorderColorClass) &&
                'before:border-gray-300',
            !pseudoElementClassNames.value.some(isAfterBorderTopColorClass) &&
                'after:border-t-blue-500',
        ];
    });

    const spinnerClass = computed(() => {
        return [compatColorClasses.value, pseudoElementClassNames.value];
    });

    const spinnerAttrs = computed(() => {
        const filteredAttrs = { ...attrs };
        delete filteredAttrs.class;
        delete filteredAttrs.style;
        return filteredAttrs;
    });

    const lineSizeToWidth: Record<number, SpinnerWidth> = {
        2: SpinnerWidth.Thin,
        3: SpinnerWidth.Medium,
        4: SpinnerWidth.Thick,
    };

    const spinnerWidth = computed(() => {
        return lineSizeToWidth[props.lineSize] ?? SpinnerWidth.Thick;
    });

    const spinnerStyle = computed(() => {
        return {
            width: `${props.size}px`,
            height: `${props.size}px`,
        };
    });
</script>
